import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from './ExpressionInput.module.scss';
import { createEditor, Editor, Transforms, Range, Text } from 'slate'
import { Slate, Editable, withReact, RenderLeafProps, RenderElementProps, ReactEditor, RenderPlaceholderProps } from 'slate-react'
import { withMentions, Element, Leaf, insertMention, Portal, createMention } from './ExpressionSlateComponents';
import { useTranslation } from 'react-i18next';
import ExpressionSystem, { ExpressionParameter } from './ExpressionSystem';
import { BODY_CLICK_IGNORE_CLASS_NAME } from 'screens/business/budgets/budget/lines/utils';
import { removeAccents } from 'common/utils/removeAccents';
import { functionDescriptions, hfOnlyFunctions, legacyAliases } from './functionDescriptions';
import { getLanguagePack } from './engine/i18n';

const FORMULA_DELIMITERS = new Set(['(', ')', '+', '-', '*', '/', '^', '=', '<', '>', ';', ',', '@', '"', "'", ' ', '\n']);

const serializeAll = (nodes: any[]) => {
    return nodes.map(serialize).join('');
}

const serialize = (node: any) => {
    if (Text.isText(node)) {
        return node.text
    }

    switch (node.type) {
        case 'mention':
            return ExpressionSystem.serializeParameterMentionId(node.id);
        default:
            return node.children.map((n: any) => serialize(n)).join('');
    }
}

const deserialize = (expression: string, parameters: ExpressionParameter[], parameterNameMap?: Record<string, string>) => {
    const regex = /M_[^,)]*_E/g;

    const result: string[] = [];
    let currentText = '';
    let i = 0;
    let inM = false;

    while (i < expression.length) {
        if (expression[i] === 'M' && expression[i + 1] === '_') {
            // If we are in the middle of processing other text, push it
            if (currentText) {
                result.push(currentText);
                currentText = '';
            }
            inM = true;
            currentText += expression[i] + expression[i + 1]; // Add 'M'
            i += 2; // Skip 'M_'
        } else if (inM && expression[i] === '_' && expression[i + 1] === 'E' && !/[0-9A-Za-z_]/.test(expression[i + 2] || '')) {
            currentText += '_E'; // Add '_E' part
            result.push(currentText); // Push the "M_..._E" to the result
            currentText = ''; // Reset for the next part
            i += 2; // Skip '_E'
            inM = false;
        } else {
            currentText += expression[i]; // Add normal character to currentText
            i++;
        }
    }

    // If there's any leftover text, push it at the end
    if (currentText) {
        result.push(currentText);
    }

    const children = result.map(x => {
        if (x.match(regex)) {
            // Try matching by ID first
            const parameter = parameters.find(p => ExpressionSystem.serializeParameterMentionId(p.id) === x);
            if (parameter) {
                return createMention(parameter.name, parameter.id);
            }

            // If not found and we have a name map, try matching by name (cross-family paste)
            if (parameterNameMap && parameterNameMap[x]) {
                const oldName = parameterNameMap[x];
                const normalizedOldName = removeAccents(oldName).toLowerCase();
                const matchByName = parameters.find(p => removeAccents(p.name).toLowerCase() === normalizedOldName);
                if (matchByName) {
                    return createMention(matchByName.name, matchByName.id);
                }
            }

            return { text: 'NOT_FOUND_ERROR' };
        }

        return { text: x }
    });

    children.push({ text: '' });

    const tree = [
        {
            type: 'paragraph',
            children,
        }
    ];

    return tree;
}

export interface ExpressionInputRef {
    updateValue: (newValue: string, parameterNameMap?: Record<string, string>) => void;
    insertTextAtCursor: (text: string) => void;
}

export interface ExpressionInputProps {
    value: string
    disabled?: boolean;
    parameters: ExpressionParameter[];
    onChange: (expression: string | null) => void;
}

interface FunctionSuggestion {
    localizedName: string;
    syntax: string;
    description: string;
}

const ExpressionInput = ({
    value,
    parameters,
    disabled,
    onChange,
}: ExpressionInputProps, selfRef: ForwardedRef<ExpressionInputRef>) => {
    const { t, i18n } = useTranslation();
    const ref = useRef<HTMLDivElement | null>(null);
    const funcRef = useRef<HTMLDivElement | null>(null);
    const [target, setTarget] = useState<Range | null>(null);
    const [index, setIndex] = useState(0);
    const [search, setSearch] = useState('');

    // Function autocomplete state
    const [funcTarget, setFuncTarget] = useState<Range | null>(null);
    const [funcIndex, setFuncIndex] = useState(0);
    const [funcSearch, setFuncSearch] = useState('');

    // Function signature hint state (shown while typing parameters)
    const [activeHint, setActiveHint] = useState<FunctionSuggestion | null>(null);

    const renderElement = useCallback(
        (props: RenderElementProps) => <Element {...props} />,
        []
    );
    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        []
    );
    const editor = useMemo(
        () => withMentions(withReact(createEditor())),
        []
    );

    const lang = i18n.language as 'pt' | 'fr';

    // Build memoized function list for autocomplete
    const functionEntries = useMemo(() => {
        const langPack = getLanguagePack(lang);
        const entries: FunctionSuggestion[] = [];
        for (const [englishKey, localizedName] of Object.entries(langPack.functions)) {
            if (!localizedName || localizedName === '') continue;
            if (hfOnlyFunctions.has(englishKey)) continue;
            if (legacyAliases.has(englishKey)) continue;
            const desc = functionDescriptions[englishKey];
            entries.push({
                localizedName,
                syntax: desc?.syntax ?? '',
                description: desc?.description?.[lang] ?? desc?.description?.pt ?? '',
            });
        }
        return entries;
    }, [lang]);

    // Lookup map for matching function names (for signature hint)
    const functionLookup = useMemo(() => {
        const map = new Map<string, FunctionSuggestion>();
        for (const entry of functionEntries) {
            map.set(removeAccents(entry.localizedName).toUpperCase(), entry);
        }
        return map;
    }, [functionEntries]);

    // Filter functions based on search text
    const filteredFunctions = useMemo(() => {
        if (!funcSearch || funcSearch.length < 2) return [];
        const normalized = removeAccents(funcSearch).toLowerCase();
        return functionEntries
            .filter(f => removeAccents(f.localizedName).toLowerCase().includes(normalized))
            .sort((a, b) => {
                const aName = removeAccents(a.localizedName).toLowerCase();
                const bName = removeAccents(b.localizedName).toLowerCase();
                const aStarts = aName.startsWith(normalized);
                const bStarts = bName.startsWith(normalized);
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;
                return aName.localeCompare(bName);
            })
            .slice(0, 10);
    }, [funcSearch, functionEntries]);

    function updateValue(newValue: string, parameterNameMap?: Record<string, string>) {
        const newExpressionTree = deserialize(newValue, parameters, parameterNameMap)

        Transforms.delete(editor, { at: [0] });
        Transforms.insertNodes(editor, newExpressionTree, { at: [0] });
    }

    function insertTextAtCursor(text: string) {
        ReactEditor.focus(editor);
        Transforms.insertText(editor, text);
    }

    useImperativeHandle(selfRef, () => ({
        updateValue,
        insertTextAtCursor,
    }));

    const chars = parameters.filter(c =>
        !c.hidden && removeAccents(c.name).toLowerCase().includes(removeAccents(search).toLowerCase())
    ); // .slice(0, 10)

    const selectFunction = useCallback((func: FunctionSuggestion) => {
        if (!funcTarget) return;
        Transforms.select(editor, funcTarget);
        Transforms.insertText(editor, func.localizedName + '(');
        setFuncTarget(null);
        ReactEditor.focus(editor);
    }, [editor, funcTarget]);

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (target && chars.length > 0) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault()
                        setIndex((_prevIndex) => {
                            const nextIndex = _prevIndex >= chars.length - 1 ? 0 : _prevIndex + 1;
                            scrollIntoView(nextIndex);
                            return nextIndex;
                        });
                        break
                    case 'ArrowUp':
                        event.preventDefault()
                        setIndex((_prevIndex) => {
                            const prevIndex = _prevIndex <= 0 ? chars.length - 1 : _prevIndex - 1;
                            scrollIntoView(prevIndex);
                            return prevIndex;
                        });
                        break
                    case 'Tab':
                    case 'Enter':
                        event.preventDefault()
                        Transforms.select(editor, target as any)
                        insertMention(editor, chars[index].name, chars[index].id)
                        setTarget(null)
                        break
                    case 'Escape':
                        event.preventDefault()
                        setTarget(null)
                        break
                }
            } else if (funcTarget && filteredFunctions.length > 0) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        setFuncIndex((_prevIndex) => {
                            const nextIndex = _prevIndex >= filteredFunctions.length - 1 ? 0 : _prevIndex + 1;
                            scrollFuncIntoView(nextIndex);
                            return nextIndex;
                        });
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        setFuncIndex((_prevIndex) => {
                            const prevIndex = _prevIndex <= 0 ? filteredFunctions.length - 1 : _prevIndex - 1;
                            scrollFuncIntoView(prevIndex);
                            return prevIndex;
                        });
                        break;
                    case 'Tab':
                    case 'Enter':
                        event.preventDefault();
                        selectFunction(filteredFunctions[funcIndex]);
                        break;
                    case 'Escape':
                        event.preventDefault();
                        setFuncTarget(null);
                        break;
                }
            }
        },
        [chars, editor, index, target, funcTarget, filteredFunctions, funcIndex, selectFunction]
    );

    const scrollIntoView = (selectedIndex: number) => {
        if (!ref.current) return;
        const suggestionItems = ref.current.querySelectorAll(`.${styles.suggestionItem}`);
        if (suggestionItems[selectedIndex]) {
            suggestionItems[selectedIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    const scrollFuncIntoView = (selectedIndex: number) => {
        if (!funcRef.current) return;
        const items = funcRef.current.querySelectorAll(`.${styles.functionSuggestionItem}`);
        if (items[selectedIndex]) {
            items[selectedIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    useEffect(() => {
        if (target && chars.length > 0 && ref.current) {
            const el = ref.current
            const domRange = ReactEditor.toDOMRange(editor, target as any)
            const rect = domRange.getBoundingClientRect()
            el.style.top = `${rect.top + window.pageYOffset + 24}px`
            el.style.left = `${rect.left + window.pageXOffset}px`
        }
    }, [chars.length, editor, index, search, target])

    useEffect(() => {
        if (funcTarget && filteredFunctions.length > 0 && funcRef.current) {
            const el = funcRef.current;
            try {
                const domRange = ReactEditor.toDOMRange(editor, funcTarget as any);
                const rect = domRange.getBoundingClientRect();
                el.style.top = `${rect.top + window.pageYOffset + 24}px`;
                el.style.left = `${rect.left + window.pageXOffset}px`;
            } catch {
                // Range may be invalid if editor state changed
            }
        }
    }, [filteredFunctions.length, editor, funcIndex, funcSearch, funcTarget]);

    return (
        <div className={styles.inputWrapper}>
            <Slate
                editor={editor}
                initialValue={deserialize(value, parameters)}
                onChange={(nodes) => {
                    const { selection } = editor

                    if (selection && Range.isCollapsed(selection)) {
                        const [start] = Range.edges(selection);

                        // Move backwards to find '@'
                        let before = start;
                        let mentionText = '';
                        while (true) {
                            const charBefore = Editor.before(editor, before, { unit: 'character' });
                            if (!charBefore) break;

                            const charRange = Editor.range(editor, charBefore, before);
                            const char = Editor.string(editor, charRange);

                            before = charBefore;
                            mentionText = char + mentionText;

                            if (char === '@') {
                                break;
                            }
                        }

                        // Ensure we found a valid '@' mention
                        if (mentionText.startsWith('@')) {
                            const beforeRange = Editor.range(editor, before, start);
                            const searchQuery = mentionText.slice(1); // Remove '@' from search query

                            // Get the text after cursor
                            const after = Editor.after(editor, start);
                            const afterRange = Editor.range(editor, start, after);
                            const afterText = Editor.string(editor, afterRange);

                            // Ensure we're at the end of the mention
                            const afterMatch = afterText.match(/^(\s|$)/) || afterText === '';

                            if (afterMatch) {
                                setTarget(beforeRange);
                                setSearch(searchQuery);
                                setIndex(0);
                                setFuncTarget(null);

                                const newValue = serializeAll(nodes);
                                onChange(newValue);
                                return;
                            }
                        }
                    }

                    setTarget(null);

                    // Function autocomplete: scan backwards for function name text
                    if (selection && Range.isCollapsed(selection)) {
                        const [start] = Range.edges(selection);

                        let before = start;
                        let funcText = '';
                        while (true) {
                            const charBefore = Editor.before(editor, before, { unit: 'character' });
                            if (!charBefore) break;

                            const charRange = Editor.range(editor, charBefore, before);
                            const char = Editor.string(editor, charRange);

                            if (FORMULA_DELIMITERS.has(char)) break;

                            before = charBefore;
                            funcText = char + funcText;
                        }

                        if (funcText.length >= 2) {
                            const funcRange = Editor.range(editor, before, start);
                            setFuncTarget(funcRange);
                            setFuncSearch(funcText);
                            setFuncIndex(0);
                        } else {
                            setFuncTarget(null);
                            setFuncSearch('');
                        }

                        // Function signature hint: detect if cursor is inside a function call
                        // Scan backwards from cursor counting parentheses to find the enclosing function
                        const fullText = Editor.string(editor, []);
                        // Calculate cursor offset by serializing up to cursor point
                        let cursorOffset = 0;
                        try {
                            const startOfDoc = Editor.start(editor, []);
                            const rangeToStart = Editor.range(editor, startOfDoc, start);
                            cursorOffset = Editor.string(editor, rangeToStart).length;
                        } catch {
                            cursorOffset = fullText.length;
                        }

                        const textBeforeCursor = fullText.slice(0, cursorOffset);
                        let depth = 0;
                        let foundHint: FunctionSuggestion | null = null;

                        for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
                            const ch = textBeforeCursor[i];
                            if (ch === ')') {
                                depth++;
                            } else if (ch === '(') {
                                if (depth === 0) {
                                    // Found the opening paren — extract function name before it
                                    const nameEnd = i;
                                    let nameStart = i - 1;
                                    while (nameStart >= 0) {
                                        const nc = textBeforeCursor[nameStart];
                                        if (FORMULA_DELIMITERS.has(nc)) break;
                                        nameStart--;
                                    }
                                    nameStart++;
                                    const funcName = textBeforeCursor.slice(nameStart, nameEnd);
                                    if (funcName.length > 0) {
                                        const normalized = removeAccents(funcName).toUpperCase();
                                        foundHint = functionLookup.get(normalized) ?? null;
                                    }
                                    break;
                                } else {
                                    depth--;
                                }
                            }
                        }

                        setActiveHint(foundHint);
                    } else {
                        setFuncTarget(null);
                        setFuncSearch('');
                        setActiveHint(null);
                    }

                    const newValue = serializeAll(nodes);
                    onChange(newValue);
                }}
            >
                <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    onKeyDown={onKeyDown}
                    placeholder={t('common.write_here')}
                    disabled={disabled}
                    readOnly={disabled}
                    renderPlaceholder={({
                        children,
                        attributes,
                    }: RenderPlaceholderProps) => (
                        <div {...attributes} className={styles.inputPlaceholder}>
                            <p>{children}</p>
                        </div>
                    )}
                    className={styles.input}
                />
                {target && chars.length > 0 && (
                    <Portal>
                        <div
                            ref={ref}
                            className={styles.suggestions}
                            data-cy="mentions-portal"
                        >
                            {chars.map((char, i) => (
                                <div
                                    key={char.id}
                                    className={`${styles.suggestionItem} ${BODY_CLICK_IGNORE_CLASS_NAME}`}
                                    onMouseDown={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        Transforms.select(editor, target)
                                        insertMention(editor, char.name, char.id)
                                        setTarget(null)
                                        ReactEditor.focus(editor)
                                    }}
                                    style={{
                                        background: i === index ? '#eef0f3' : 'transparent',
                                    }}
                                >
                                    {char.name}
                                </div>
                            ))}
                        </div>
                    </Portal>
                )}
                {funcTarget && filteredFunctions.length > 0 && (
                    <Portal>
                        <div
                            ref={funcRef}
                            className={styles.functionSuggestions}
                            data-cy="functions-portal"
                        >
                            {filteredFunctions.map((func, i) => (
                                <div
                                    key={func.localizedName}
                                    className={`${styles.functionSuggestionItem} ${BODY_CLICK_IGNORE_CLASS_NAME}`}
                                    onMouseDown={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        selectFunction(func);
                                    }}
                                    style={{
                                        background: i === funcIndex ? '#eef0f3' : 'transparent',
                                    }}
                                >
                                    <div className={styles.funcName}>
                                        {func.localizedName}
                                        <span className={styles.funcSyntax}>{func.syntax}</span>
                                    </div>
                                    {func.description && (
                                        <div className={styles.funcDescription}>{func.description}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Portal>
                )}
                {activeHint && !disabled && !target && !(funcTarget && filteredFunctions.length > 0) && (
                    <div className={styles.functionHint} onMouseDown={(e) => e.preventDefault()}>
                        <span className={styles.hintName}>{activeHint.localizedName}</span>
                        <span className={styles.hintSyntax}>{activeHint.syntax}</span>
                        {activeHint.description && (
                            <div className={styles.hintDescription}>{activeHint.description}</div>
                        )}
                    </div>
                )}
            </Slate>
        </div>
    );
};

export default forwardRef<ExpressionInputRef, ExpressionInputProps>(ExpressionInput);
